import { useState, useEffect } from "react";
import api, { apiErr } from "@/lib/api";
import { uploadArtFile, artUrl } from "@/lib/uploadArt";
import InkEstimateCard from "@/components/studio/InkEstimateCard";
import SaveToLibraryButton from "@/components/studio/SaveToLibraryButton";
import { Button } from "@/components/ui/button";
import { HelpHint } from "@/components/HelpHint";
import { Upload, CheckCircle2, Loader2, FileDown } from "lucide-react";
import { toast } from "sonner";

export const FitTemplateTab = () => {
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [file, setFile] = useState(null);
  const [uploaded, setUploaded] = useState(null); // { file_id }
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/studio/templates").then((r) => setTemplates(r.data.templates || [])).catch(() => {});
  }, []);

  const onPick = async (f) => {
    if (!f) return;
    setFile(f); setResult(null); setUploaded(null);
    try {
      setBusy(true);
      const up = await uploadArtFile(f);
      setUploaded(up);
      toast.success("Client file uploaded");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const generate = async () => {
    if (!templateId) { toast.error("Pick a template"); return; }
    if (!uploaded?.file_id) { toast.error("Upload the client file first"); return; }
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("template_id", templateId);
      fd.append("file_id", uploaded.file_id);
      const r = await api.post("/studio/fit-to-template", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data);
      toast.success("Print-ready PDF generated");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const tmpl = templates.find((t) => t.id === templateId);

  return (
    <div className="space-y-4 max-w-3xl" data-testid="fit-template-tab">
      <p className="text-sm text-slate-500 flex items-center gap-2">Drop a client's ready file into one of your templates.
        <HelpHint side="right">Pick a template, upload the client's ready PDF/image, and generate a print-ready PDF placed into your template (auto-rotated per slot, bleed included, guides removed). For files with 2 pages, use a 2-side template.</HelpHint>
      </p>

      <div>
        <label className="text-[11px] font-mono uppercase text-slate-500 flex items-center gap-1 mb-1">Template
          <HelpHint side="top">Choose which of your saved templates to use. Manage them in the <b>Templates</b> tab.</HelpHint>
        </label>
        <select className="w-full h-10 rounded-lg border px-2 text-sm" data-testid="fit-template-select" value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
          <option value="">Select a template…</option>
          {templates.map((t) => <option key={t.id} value={t.id}>{t.name} — {t.trim_w}×{t.trim_h}{t.unit} · {t.sides} side(s)</option>)}
        </select>
      </div>

      <label className="flex flex-col items-center justify-center gap-2 h-32 rounded-xl border-2 border-dashed border-slate-300 cursor-pointer hover:border-[#2495D3] text-sm text-slate-500 bg-slate-50/50" data-testid="fit-drop">
        {file ? <><CheckCircle2 size={20} className="text-emerald-500" /><span className="max-w-[240px] truncate">{file.name}</span><span className="text-[11px] text-slate-400">Click to replace</span></>
              : <><Upload size={22} /><span>Upload client file (PDF / PNG / JPG)</span></>}
        <input data-testid="fit-file" type="file" accept="application/pdf,image/png,image/jpeg" className="hidden" onChange={(e) => { onPick(e.target.files?.[0]); e.target.value = ""; }} />
      </label>

      <Button onClick={generate} disabled={busy || !templateId || !uploaded} data-testid="fit-generate">
        {busy ? <><Loader2 size={16} className="animate-spin" /> Working…</> : "Build print-ready PDF"}
      </Button>

      {tmpl && uploaded?.file_id && <InkEstimateCard fileId={uploaded.file_id} width={tmpl.trim_w} height={tmpl.trim_h} bleed={tmpl.bleed || 0} unit={tmpl.unit || "in"} quantity={tmpl.sides || 1} tab="fit" auto />}

      {result && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-4 flex items-center justify-between" data-testid="fit-result">
          <div className="text-sm"><b>{result.filename}</b><div className="text-xs text-slate-500">{result.sides} side(s) · guides removed</div></div>
          <a href={artUrl(result.file_id)} target="_blank" rel="noreferrer" download>
            <Button size="sm" data-testid="fit-download"><FileDown size={16} /> Download</Button>
          </a>
        </div>
      )}
      {result?.file_id && (
        <div className="flex justify-end mt-2" data-testid="fit-save-library">
          <SaveToLibraryButton fileId={result.file_id} defaultLabel={result.filename} />
        </div>
      )}
    </div>
  );
};

export default FitTemplateTab;
